import { VisitReason, visitReasonLabels, type ComplaintHeader } from "@/store/primaryComplaint";

/** "Lower back pain" from the body region and visit reason, or "" when neither is known. */
export function complaintName(region: string | null | undefined, complaintType: string | null): string {
  const reason = complaintType ? visitReasonLabels[complaintType as VisitReason] ?? complaintType : "";
  const name = [region?.trim(), reason].filter(Boolean).join(" ");
  return name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : "";
}

/** One screen in a single complaint's path, from its first question to the recap. */
export type ComplaintStepKey =
  | "history1"
  | "history2"
  | "history3"
  | "factors"
  | "dedicated"
  | "outcome"
  | "recap";

const PAIN_STEPS: ComplaintStepKey[] = ["history1", "history2", "history3", "factors", "outcome", "recap"];
const BODY_STEPS: ComplaintStepKey[] = ["history1", "history2", "factors", "outcome", "recap"];
const DEDICATED_STEPS: ComplaintStepKey[] = ["dedicated", "outcome", "recap"];

/**
 * The screens a complaint of this type actually walks through. Only pain asks about radiation
 * (history3); headaches / dizziness / wellness use one dedicated screen instead of the history steps.
 */
export function complaintStepList(complaintType: string | null | undefined): ComplaintStepKey[] {
  switch ((complaintType ?? "").toLowerCase()) {
    case VisitReason.PAIN:
      return PAIN_STEPS;
    case VisitReason.HEADACHES:
    case VisitReason.DIZZINESS:
    case VisitReason.WELLNESS:
      return DEDICATED_STEPS;
    default:
      return BODY_STEPS;
  }
}

/** 1-based position of `key` in this complaint's path, or null when the path never visits it. */
export function complaintStepPosition(
  complaintType: string | null | undefined,
  key: ComplaintStepKey
): { index: number; total: number } | null {
  const steps = complaintStepList(complaintType);
  const i = steps.indexOf(key);
  return i === -1 ? null : { index: i + 1, total: steps.length };
}

/**
 * The complaint's real type. After a refresh the history screens rebuild the current header from the
 * URL with a blank type, so prefer the loaded complaint list and only then the persisted visit reason.
 */
export function resolveComplaintType(
  headers: ComplaintHeader[] | null | undefined,
  complaintId: string | number | null | undefined,
  fallback: string | null | undefined
): string | null {
  if (complaintId != null) {
    const header = headers?.find((h) => String(h.id) === String(complaintId));
    const type = header?.complaintType?.trim();
    if (type) return type.toLowerCase();
  }
  return fallback ? fallback.toLowerCase() : null;
}

/** 1-based number of this complaint among the patient's complaints, or null if it isn't listed yet. */
export function complaintOrdinal(
  headers: ComplaintHeader[] | null | undefined,
  complaintId: string | number | null | undefined
): number | null {
  if (complaintId == null || !headers?.length) return null;
  const i = headers.findIndex((h) => String(h.id) === String(complaintId));
  return i === -1 ? null : i + 1;
}

export interface RequiredQuestion {
  /** DOM id of the question's fieldset, so Continue can scroll to and focus it. */
  id: string;
  answered: boolean;
}

/** Id of the first required question still unanswered, in screen order; null when all are answered. */
export function missingRequired(questions: RequiredQuestion[]): string | null {
  return questions.find((q) => !q.answered)?.id ?? null;
}

/** Reasons answered on their own screen instead of the body map + shared complaint-history steps. */
export const DEDICATED_REASON_ROUTES: Partial<Record<VisitReason, string>> = {
  [VisitReason.HEADACHES]: "/complaint/headache-history",
  [VisitReason.DIZZINESS]: "/complaint/dizziness",
  [VisitReason.WELLNESS]: "/complaint/wellness",
};

/** The value saved for a field when the step loaded, or "" when none was saved. */
export function savedValue(values: { code: string; value?: string | null }[] | null | undefined, code: string): string {
  return values?.find((v) => v.code === code)?.value ?? "";
}
